import apiV2 from '../api/v2';
import type { PaginatedMeta } from './accounts';

// Accounts > Practice detail > Timeline tab. One newest-first feed of what
// happened to a practice: admin edits to its info, and the communications
// logged against it. Backed by GET /practices/:practiceId/timeline.
//
// Communication entries are merged in server-side from
// practice_communication_entry rather than duplicated into the event table,
// so the whole history is present from day one and can never drift from the
// communications themselves. The wire kind stays 'call' — the backend
// preserved the timeline's shape when the underlying table moved.

export type PracticeTimelineKind = 'practice_update' | 'call';

/** One field that changed in a practice edit. Null means empty, not unknown. */
export interface PracticeTimelineChange {
    field: string;
    label: string;
    before: string | null;
    after: string | null;
}

export interface PracticeTimelineCall {
    referenceNo: string;
    title: string;
    status: string;
    categories: string[];
    callerName: string | null;
}

export interface PracticeTimelineEntry {
    id: string;
    kind: PracticeTimelineKind;
    /** Event type for an update ('practice.updated'); direction for a call. */
    subtype: string;
    occurredAt: string;
    actorName: string | null;
    /** Non-null exactly when kind is 'practice_update'. */
    changes: PracticeTimelineChange[] | null;
    /** Non-null exactly when kind is 'call'. */
    call: PracticeTimelineCall | null;
}

export async function fetchPracticeTimeline(
    practiceId: string,
    page: number,
    limit: number
): Promise<{ data: PracticeTimelineEntry[]; meta: PaginatedMeta }> {
    const response = await apiV2.get<{
        success: true;
        data: PracticeTimelineEntry[];
        meta: PaginatedMeta;
    }>(`/practices/${practiceId}/timeline`, { params: { page, limit } });
    return { data: response.data.data, meta: response.data.meta };
}
